'use client';

/* eslint-disable react/prop-types */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import { useUser } from '@/contexts/UserContext';
import {
  Search,
  Filter,
  Package,
  Check,
  ArrowUpDown,
  X,
  Save,
  Download,
  Upload,
  AlertTriangle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Your API functions and other components
import { inventoryApi } from '@/pages/vendor/api/InventoryApi';
import { API_URL, USER_ROLES } from '@/constant/constant';
import { toast } from 'react-toastify';
import axiosInstance from '../../utils/axiosInstance';

// A simple hook to check for media queries
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    window.addEventListener('resize', listener);
    return () => window.removeEventListener('resize', listener);
  }, [matches, query]);

  return matches;
};

// ProductCard with variant pricing preview
const ProductCard = ({ product, changedVariants, onEditClick }) => {
  const [selectedVariant, setSelectedVariant] = useState(
    product.productVariants?.length > 0 ? product.productVariants[0] : null
  );

  useEffect(() => {
    // This effect syncs the selected variant if the product prop is updated externally (e.g., after a save)
    if (selectedVariant) {
      const updatedVariant = product.productVariants?.find((v) => v.id === selectedVariant.id);
      if (updatedVariant) {
        setSelectedVariant(updatedVariant);
      }
    } else if (product.productVariants?.length > 0) {
      setSelectedVariant(product.productVariants[0]);
    }
  }, [product, selectedVariant]);

  const displayImageUrl =
    product?.productImageUrls && product.productImageUrls.length > 0
      ? product.productImageUrls[0]
      : '/images/imagenotfound.webp';

  const hasChangedVariants = product.productVariants?.some((variant) =>
    changedVariants.has(`${product.productId}-${variant.id}`)
  );

  const isProductAvailable = product.productVariants?.some((v) => v.available) ?? false;

  return (
    <div
      className={`rounded-lg border-2 overflow-hidden transition-all duration-300 shadow-sm hover:shadow-lg cursor-pointer group ${
        hasChangedVariants
          ? 'border-blue-600 bg-blue-50 shadow-blue-100'
          : isProductAvailable
            ? 'border-green-300 bg-green-50 hover:border-[#F77C06] hover:shadow-orange-100'
            : 'border-red-300 bg-red-50'
      }`}
      onClick={() => {
        onEditClick(product);
      }}
    >
      <div className="flex items-start gap-3 p-2 transition-all duration-200 group-hover:bg-slate-50">
        <div className="flex flex-col">
          <div className="relative flex-shrink-0">
            <img
              src={displayImageUrl || '/images/imagenotfound.webp'}
              alt={product?.productName}
              onError={(e) => {
                e.target.src = '/images/imagenotfound.webp';
              }}
              className={`size-16 rounded-lg object-cover border-2 transition-all duration-200 ${
                isProductAvailable
                  ? 'border-slate-200 group-hover:border-[#F77C06]'
                  : 'border-red-300 opacity-60'
              }`}
            />

            {hasChangedVariants && (
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center shadow-lg">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              </div>
            )}

            <div
              className={`absolute -top-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${
                isProductAvailable ? 'bg-green-500' : 'bg-red-500'
              }`}
            />
          </div>
        </div>

        <div className="flex flex-col flex-1">
          <h3 className="font-bold text-slate-900 text-sm leading-tight line-clamp-2 group-hover:text-[#F77C06] transition-colors">
            {product?.productName}
          </h3>

          <div className="text-sm font-semibold text-slate-700 px-1.5 rounded-md truncate">
            {selectedVariant?.variant} {selectedVariant?.unit}
          </div>
        </div>
      </div>{' '}
    </div>
  );
};

const ActiveFiltersDisplay = ({ activeFilters, onRemoveFilter, onClearAll }) => {
  const filterConfig = {
    tags: { style: 'bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-300' },
    authors: { style: 'bg-indigo-100 text-indigo-800 border-indigo-200 hover:bg-indigo-300' },
    genres: { style: 'bg-cyan-100 text-cyan-800 border-cyan-200 hover:bg-cyan-300' },
    publishers: { style: 'bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-300' },
    languages: { style: 'bg-teal-100 text-teal-800 border-teal-200 hover:bg-teal-300' },
    collections: { style: 'bg-rose-100 text-rose-800 border-rose-200 hover:bg-rose-300' },
    institutions: {
      style: 'bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-300',
    },
    brands: { style: 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-300' },
  };

  const allFilters = Object.entries(activeFilters).flatMap(([type, values]) =>
    values.map((value) => ({ type, value }))
  );

  if (allFilters.length === 0) {
    return null;
  }

  return (
    <div className="px-3 pt-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold text-slate-700">Applied Filters:</span>
        {allFilters.map(({ type, value }) => (
          <Badge
            key={`${type}-${value}`}
            variant="secondary"
            className={`flex items-center gap-1 pl-3 pr-1 py-1 text-sm border ${filterConfig[type]?.style}`}
          >
            {value}
            <button
              onClick={() => onRemoveFilter(type, value)}
              className={`rounded-full p-0.5 transition-colors ${filterConfig[type]?.style.split(' ')[4]}`} // Grabs the hover color
              aria-label={`Remove ${value} filter`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </Badge>
        ))}
        {allFilters.length > 1 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearAll}
            className="h-auto px-2 py-1 text-xs text-red-600 hover:bg-red-100 hover:text-red-700 font-bold"
          >
            Clear All
          </Button>
        )}
      </div>{' '}
    </div>
  );
};

const FilterSheet = ({ availableFilters, selectedFilters, onApply, isOpen, onOpenChange }) => {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const sheetSide = isDesktop ? 'right' : 'bottom';

  // Configuration for filter types
  const filterConfig = [
    { key: 'tags', label: 'Tags' },
    { key: 'authors', label: 'Authors' },
    { key: 'genres', label: 'Genres' },
    { key: 'publishers', label: 'Publishers' },
    { key: 'languages', label: 'Languages' },
    { key: 'collections', label: 'Collections' },
    { key: 'institutions', label: 'Institutions' },
    { key: 'brands', label: 'Brands' },
  ].filter((config) => availableFilters[config.key]?.length > 0);

  const initialTempState = () => {
    const state = {};
    filterConfig.forEach((config) => {
      state[config.key] = selectedFilters[config.key] || [];
    });
    return state;
  };

  const [tempSelections, setTempSelections] = useState(initialTempState());

  useEffect(() => {
    if (isOpen) {
      setTempSelections(initialTempState());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedFilters]);

  const handleTempToggle = (type, value) => {
    setTempSelections((prev) => {
      const currentValues = prev[type] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];
      return { ...prev, [type]: newValues };
    });
  };

  const handleClear = () => {
    const clearedState = {};
    filterConfig.forEach((config) => {
      clearedState[config.key] = [];
    });
    setTempSelections(clearedState);
    onApply(clearedState);
  };

  const handleApply = () => {
    onApply(tempSelections);
  };

  const totalSelectedCount = Object.values(selectedFilters).reduce(
    (acc, curr) => acc + curr.length,
    0
  );
  const tempChangesCount = Object.values(tempSelections).reduce(
    (acc, curr) => acc + curr.length,
    0
  );
  const noFiltersAvailable = filterConfig.length === 0;

  return (
    <Sheet open={isOpen} className="gap-0" onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <Button variant="outline" className="relative border-2 border-slate-300 bg-transparent">
          <Filter className="h-4 w-4 sm:mr-2" />
          <span className="font-semibold sm:block hidden">Filters</span>
          {totalSelectedCount > 0 && (
            <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center bg-[#F77C06] text-white">
              {totalSelectedCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent
        side={sheetSide}
        className={`bg-slate-50 flex flex-col p-0 ${isDesktop ? 'w-full max-w-sm' : 'w-full h-[70vh] rounded-t-2xl'}`}
      >
        <SheetHeader className="p-4 border-b bg-white">
          <SheetTitle className="flex items-center gap-2 text-slate-900">
            <Filter className="h-5 w-5 text-[#F77C06]" /> Filters{' '}
          </SheetTitle>
        </SheetHeader>

        {noFiltersAvailable ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
            <Filter className="h-12 w-12 mx-auto mb-3 text-slate-400" />
            <p className="text-sm font-medium text-slate-600">
              No filters available for this category
            </p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {filterConfig.map((config) => (
              <div key={config.key} className="border-b border-slate-200 last:border-b-0">
                <div className="p-4 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-slate-900">{config.label}</h3>
                    <Badge
                      variant={
                        (tempSelections[config.key]?.length || 0) > 0 ? 'default' : 'secondary'
                      }
                      className={
                        (tempSelections[config.key]?.length || 0) > 0 ? 'bg-[#F77C06]' : ''
                      }
                    >
                      {tempSelections[config.key]?.length || 0} selected
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {(availableFilters[config.key] || []).map((value) => (
                      <button
                        key={value}
                        onClick={() => handleTempToggle(config.key, value)}
                        className={`flex items-center justify-center text-center px-3 py-2 text-xs rounded-md border-2 transition-all font-semibold ${
                          (tempSelections[config.key] || []).includes(value)
                            ? 'bg-[#F77C06] border-[#F77C06] text-white'
                            : 'bg-white border-slate-300 text-slate-700 hover:border-[#F77C06]'
                        }`}
                      >
                        {(tempSelections[config.key] || []).includes(value) && (
                          <Check className="h-3 w-3 mr-1.5" />
                        )}
                        <span>{value}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!noFiltersAvailable && (
          <DialogFooter className="p-4 border-t bg-white/80 backdrop-blur-sm flex gap-2">
            <Button
              variant="outline"
              onClick={handleClear}
              className="flex-1 border-2 border-slate-400 text-slate-700 font-bold bg-transparent"
              disabled={tempChangesCount === 0}
            >
              Clear All
            </Button>

            <Button
              onClick={handleApply}
              className="flex-1 bg-[#F77C06] hover:bg-[#E66A00] text-white font-bold"
            >
              Apply Filters ({tempChangesCount})
            </Button>
          </DialogFooter>
        )}
      </SheetContent>{' '}
    </Sheet>
  );
};

// Excel Export/Import Component
// const ExcelActions = ({ products, user, onImportSuccess }) => {
//   const fileInputRef = useRef(null);
//   const [isImporting, setIsImporting] = useState(false);

//   const handleExport = () => {
//     if (!products || products.length === 0) {
//       toast.error('No products to export');
//       return;
//     }

//     // Flatten products with variants into rows
//     const exportData = products.flatMap(
//       (product) =>
//         product.productVariants?.map((variant) => ({
//           productName: product.productName || '',
//           variantName: variant?.variant || '',
//           mrp: variant?.mrp || 0,
//           netPrice: variant?.netPrice || 0,
//           unit: variant?.unit || '',
//           available: variant?.available ? 'TRUE' : 'FALSE',
//         })) || []
//     );

//     if (exportData.length === 0) {
//       toast.error('No product variants to export');
//       return;
//     }

//     const worksheet = XLSX.utils.json_to_sheet(exportData);
//     const workbook = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(workbook, worksheet, 'Products');

//     // Set column widths
//     worksheet['!cols'] = [
//       { wch: 40 }, // productName
//       { wch: 25 }, // variantName
//       { wch: 10 }, // mrp
//       { wch: 10 }, // netPrice
//       { wch: 15 }, // unit
//       { wch: 10 }, // available
//     ];

//     const fileName = `products_export_${new Date().toISOString().split('T')[0]}.xlsx`;
//     XLSX.writeFile(workbook, fileName);
//     toast.success(`Exported ${exportData.length} product variants`);
//   };

//   const handleImportClick = () => {
//     fileInputRef.current?.click();
//   };

//   const handleFileChange = async (e) => {
//     const file = e.target.files?.[0];
//     if (!file) return;

//     // Validate file type
//     const validTypes = [
//       'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
//       'application/vnd.ms-excel',
//     ];
//     if (
//       !validTypes.includes(file.type) &&
//       !file.name.endsWith('.xlsx') &&
//       !file.name.endsWith('.xls')
//     ) {
//       toast.error('Please upload a valid Excel file (.xlsx or .xls)');
//       e.target.value = '';
//       return;
//     }

//     setIsImporting(true);

//     try {
//       // Create FormData for API
//       const formData = new FormData();
//       formData.append('files', file);

//       await axios.post(
//         `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/upload?X-Vendor-Id=${user?.id}`,
//         formData,
//         {
//           headers: {
//             sessionToken: localStorage.getItem('sessionToken'),
//             'X-USER-ROLE': USER_ROLES?.VENDOR,
//             'Content-Type': 'multipart/form-data',
//           },
//         }
//       );

//       toast.success('Products imported successfully!');
//       onImportSuccess?.();
//     } catch (error) {
//       console.error('Import error:', error);
//       toast.error(
//         error.response?.data?.message || 'Failed to import products. Please check the file format.'
//       );
//     } finally {
//       setIsImporting(false);
//       e.target.value = '';
//     }
//   };

//   return (
//     <div className="flex gap-2">
//       <Button
//         variant="outline"
//         onClick={handleExport}
//         className="border-2 border-green-500 text-green-600 hover:bg-green-50 bg-transparent"
//         title="Export to Excel"
//       >
//         <Download className="h-4 w-4 sm:mr-2" />
//         <span className="font-semibold sm:block hidden">Export</span>
//       </Button>

//       <Button
//         variant="outline"
//         onClick={handleImportClick}
//         disabled={isImporting}
//         className="border-2 border-blue-500 text-blue-600 hover:bg-blue-50 bg-transparent"
//         title="Import from Excel"
//       >
//         <Upload className="h-4 w-4 sm:mr-2" />
//         <span className="font-semibold sm:block hidden">
//           {isImporting ? 'Importing...' : 'Import'}
//         </span>
//       </Button>

//       <input
//         ref={fileInputRef}
//         type="file"
//         accept=".xlsx,.xls"
//         onChange={handleFileChange}
//         className="hidden"
//       />
//     </div>
//   );
// };

const SortDropdown = ({ currentSort, onSortChange }) => {
  const sortOptions = [
    { value: 'default', label: 'Default' },
    { value: 'name-asc', label: 'Name (A-Z)' },
    { value: 'name-desc', label: 'Name (Z-A)' },
  ];

  const currentLabel = sortOptions.find((opt) => opt.value === currentSort)?.label || 'Sort';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="border-2 border-slate-300 bg-transparent">
          <ArrowUpDown className="h-4 w-4 sm:mr-2" />
          <span className="font-semibold sm:block hidden">{currentLabel}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {sortOptions.map((option) => (
          <DropdownMenuItem key={option.value} onSelect={() => onSortChange(option.value)}>
            <div className="flex items-center w-full">
              <span className="w-6">
                {option.value === currentSort && <Check className="h-4 w-4" />}
              </span>
              <span>{option.label}</span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>{' '}
    </DropdownMenu>
  );
};

const CategoryTabs = ({ categories, selectedCategory, onSelect }) => {
  const scrollContainerRef = useRef(null);

  const handleSelectAndScroll = (categoryId, event) => {
    onSelect(categoryId);
    if (scrollContainerRef.current && event.currentTarget) {
      const container = scrollContainerRef.current;
      const button = event.currentTarget;
      const containerWidth = container.offsetWidth;
      const buttonWidth = button.offsetWidth;
      const buttonOffsetLeft = button.offsetLeft;
      const scrollTo = buttonOffsetLeft - containerWidth / 2 + buttonWidth / 2;
      container.scrollTo({
        left: scrollTo,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      <style>{scrollbarHideStyle}</style>
      <div
        ref={scrollContainerRef}
        className="overflow-x-auto hide-scrollbar border-slate-200 bg-white"
      >
        <div className="flex items-center px-4 pb-0 gap-6 min-w-max">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={(event) => handleSelectAndScroll(category.id, event)}
              className={`relative flex flex-col items-center justify-center gap-0 py-3 px-1 text-sm font-bold transition-all ${
                selectedCategory === category.id
                  ? 'text-[#F77C06]'
                  : 'text-slate-700 hover:text-[#F77C06]'
              }`}
            >
              {category?.name}
              {selectedCategory === category?.id && (
                <span className="absolute bottom-0 left-0 right-0 h-1 bg-[#F77C06] rounded-full" />
              )}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

const SkeletonCard = () => (
  <div className="bg-white rounded-md border-2 border-slate-200 animate-pulse p-2">
    {' '}
    <div className="flex items-start gap-3">
      <div className="size-12 rounded-lg bg-slate-200 flex-shrink-0"></div>{' '}
      <div className="flex-1 space-y-3 mt-1">
        <div className="h-5 bg-slate-200 rounded-lg w-3/4"></div>
        <div className="h-4 bg-slate-200 rounded w-1/2"></div>{' '}
      </div>{' '}
    </div>{' '}
  </div>
);

const ProductListSkeleton = () => (
  <>
    <SkeletonCard />
    <SkeletonCard />
    <SkeletonCard />
    <SkeletonCard />
    <SkeletonCard />
    <SkeletonCard />{' '}
  </>
);

const EmptyState = ({ hasSearch, hasFilters }) => (
  <div className="col-span-full flex flex-col items-center justify-center text-center py-20">
    {' '}
    <div className="w-24 h-24 bg-slate-100 rounded-3xl flex items-center justify-center mb-6">
      <Package className="h-12 w-12 text-slate-400" />{' '}
    </div>
    <h3 className="text-xl font-bold text-slate-800 mb-2">No Products Found</h3>{' '}
    <p className="text-slate-600 max-w-md font-medium">
      {hasSearch || hasFilters
        ? "We couldn't find any products matching your criteria. Try adjusting your search or clearing the filters."
        : "There are no products in this category yet. Products will appear here once they're added."}{' '}
    </p>{' '}
  </div>
);

const SubCategoryButton = ({ isSelected, onClick, name }) => (
  <button
    onClick={onClick}
    className={` flex-shrink-0 px-2 py-1.5 overflow-hidden rounded-md transition-all duration-200 border-2 ${
      isSelected
        ? 'bg-[#F77C06] border-[#F77C06] text-white font-bold shadow-lg'
        : 'bg-white border-slate-300 text-slate-700 hover:border-[#F77C06] hover:bg-orange-50'
    }`}
  >
    <span className="text-xs text-center leading-tight line-clamp-2 font-bold">{name}</span>{' '}
  </button>
);

const scrollbarHideStyle = `
 .hide-scrollbar::-webkit-scrollbar {
  display: none;
 }
 .hide-scrollbar {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
 }
`;

const SubCategoryMenu = ({ subCategories, selectedSubCategory, onSelect }) => {
  const scrollContainerRef = useRef(null);
  const handleSelectAndScroll = (subId, event) => {
    onSelect(subId);
    if (scrollContainerRef.current && event.currentTarget) {
      const container = scrollContainerRef.current;
      const button = event.currentTarget;
      const containerWidth = container.offsetWidth;
      const buttonWidth = button.offsetWidth;
      const buttonOffsetLeft = button.offsetLeft;
      const scrollTo = buttonOffsetLeft - containerWidth / 2 + buttonWidth / 2;
      container.scrollTo({
        left: scrollTo,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      <style>{scrollbarHideStyle}</style>
      <aside className="w-full bg-white border-b-2 border-slate-200 py-1 px-2 sm:py-2 sm:px-3">
        <div
          ref={scrollContainerRef}
          className="flex items-center gap-2 w-full overflow-x-auto flex-nowrap hide-scrollbar"
        >
          {subCategories.map((sub) => (
            <SubCategoryButton
              key={sub.id}
              isSelected={selectedSubCategory === sub.id}
              onClick={(event) => handleSelectAndScroll(sub.id, event)}
              name={sub.name}
            />
          ))}
        </div>
      </aside>
    </>
  );
};

// =================================================================
// HELPER FUNCTION: To find the first leaf node in a category tree.
// A "leaf node" is a category that has no sub-categories.
// =================================================================
const findFirstLeafNode = (category) => {
  if (!category) return null;
  let currentNode = category;
  while (currentNode?.subCategories && currentNode.subCategories.length > 0) {
    currentNode = currentNode.subCategories[0];
  }
  return currentNode;
};

export default function ProductPricingPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [originalProducts, setOriginalProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedSubSubCategory, setSelectedSubSubCategory] = useState(null);
  const [changedVariants, setChangedVariants] = useState(new Set());
  const [changedProducts, setChangedProducts] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [subCategories, setSubCategories] = useState([]);
  const { user } = useUser();
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [sortOption, setSortOption] = useState('default');
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [subSubCategories, setSubSubCategories] = useState([]);
  const [showAccessErrorDialog, setShowAccessErrorDialog] = useState(false);

  // State for all available filters
  const [availableFilters, setAvailableFilters] = useState({
    tags: [],
    authors: [],
    genres: [],
    publishers: [],
    languages: [],
    collections: [],
    institutions: [],
    brands: [],
  });

  const [selectedFilters, setSelectedFilters] = useState({
    tags: [],
    authors: [],
    genres: [],
    publishers: [],
    languages: [],
    collections: [],
    institutions: [],
    brands: [],
  });

  const lastFetchedLeafId = useRef(null);

  const extractFiltersFromProducts = (productsData) => {
    const filters = {
      tags: new Set(),
      authors: new Set(),
      genres: new Set(),
      publishers: new Set(),
      languages: new Set(),
      collections: new Set(),
      institutions: new Set(),
      brands: new Set(),
    };

    const filterKeys = {
      Tag: 'tags',
      Author: 'authors',
      Genre: 'genres',
      Publisher: 'publishers',
      Language: 'languages',
      Collection: 'collections',
      Institution: 'institutions',
    };

    productsData.forEach((product) => {
      product.productAttributes?.forEach((attr) => {
        const filterType = filterKeys[attr?.name];
        if (filterType && attr?.value) {
          filters[filterType].add(attr.value);
        }
      });

      // Add brand filter
      if (product.brand) {
        filters.brands.add(product.brand);
      }
    });

    setAvailableFilters({
      tags: Array.from(filters.tags).sort(),
      authors: Array.from(filters.authors).sort(),
      genres: Array.from(filters.genres).sort(),
      publishers: Array.from(filters.publishers).sort(),
      languages: Array.from(filters.languages).sort(),
      collections: Array.from(filters.collections).sort(),
      institutions: Array.from(filters.institutions).sort(),
      brands: Array.from(filters.brands).sort(),
    });
  };

  const fetchCategoriesAndProducts = async () => {
    if (!user?.vendorCategories || user.vendorCategories.length === 0) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const vendorCategories = user.vendorCategories;
    setCategories(vendorCategories);

    if (vendorCategories.length > 0) {
      const firstCategory = vendorCategories[0];
      const leafNode = findFirstLeafNode(firstCategory);

      if (leafNode) {
        const firstSubCategory = firstCategory.subCategories?.[0];
        const firstSubSubCategory = firstSubCategory?.subCategories?.[0];

        setSelectedCategory(firstCategory.id);
        setSubCategories(firstCategory.subCategories || []);
        setSelectedSubCategory(firstSubCategory?.id || null);
        setSubSubCategories(firstSubCategory?.subCategories || []);
        setSelectedSubSubCategory(firstSubSubCategory?.id || null);
        await fetchProducts(firstCategory.id, leafNode.id);
      } else {
        setProducts([]);
        setOriginalProducts([]);
      }
    }
    setLoading(false);
  };

  const fetchProducts = useCallback(
    async (categoryId, leafCategoryId) => {
      if (leafCategoryId === lastFetchedLeafId.current) {
        return;
      }

      if (!user?.id || !categoryId || !leafCategoryId) return;
      try {
        setLoading(true);
        const result = await inventoryApi.fetchProductsByCategory(
          user.id,
          categoryId,
          leafCategoryId
        );
        if (result.success) {
          const processedProducts = result.data.map((product) => ({
            ...product,
            productVariants:
              product.productVariants?.map((variant) => ({
                ...variant,
                mrp: variant.mrp || 0,
                netPrice: variant.netPrice || 0,
                available: variant.available === true,
              })) || [],
          }));
          setProducts(processedProducts);
          // setOriginalProducts(JSON.parse(JSON.stringify(processedProducts)));
          setOriginalProducts(structuredClone(processedProducts));

          setChangedVariants(new Set());
          extractFiltersFromProducts(processedProducts);
          // Reset all selected filters on new product fetch
          setSelectedFilters({
            tags: [],
            authors: [],
            genres: [],
            publishers: [],
            languages: [],
            collections: [],
            institutions: [],
            brands: [],
          });
          setIsFilterSheetOpen(false);
          lastFetchedLeafId.current = leafCategoryId;
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        setProducts([]);
        setOriginalProducts([]);
        lastFetchedLeafId.current = null;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const handleSaveProduct = async (editedProduct) => {
    const newProducts = products.map((p) =>
      p.productId === editedProduct.productId ? editedProduct : p
    );

    setProducts(newProducts);

    setChangedProducts((prev) => {
      const newMap = new Map(prev);
      newMap.set(editedProduct.productId, editedProduct);
      return newMap;
    });

    const originalProd = originalProducts.find((p) => p.productId === editedProduct.productId);

    setChangedVariants((prev) => {
      const newSet = new Set(prev);
      editedProduct.productVariants.forEach((variant) => {
        const originalVariant = originalProd?.productVariants.find((v) => v.id === variant.id);
        const variantKey = `${editedProduct.productId}-${variant.id}`;

        const hasChanged =
          originalVariant &&
          (originalVariant.mrp?.toString() !== variant.mrp?.toString() ||
            originalVariant.netPrice?.toString() !== variant.netPrice?.toString() ||
            originalVariant.available !== variant.available);

        if (hasChanged) {
          newSet.add(variantKey);
        } else {
          newSet.delete(variantKey);
        }
      });

      // Also check if alias changed at product level
      if ((originalProd?.alias || '') !== (editedProduct.alias || '')) {
        // Mark all variants as changed if alias changed
        editedProduct.productVariants.forEach((variant) => {
          const variantKey = `${editedProduct.productId}-${variant.id}`;
          newSet.add(variantKey);
        });
      }

      return newSet;
    });
  };

  const handleBulkSave = async () => {
    if (changedProducts.size === 0) return;
    setIsSaving(true);
    try {
      const allApiData = Array.from(changedProducts.values()).flatMap((editedProduct) =>
        editedProduct.productVariants.map((variant) => ({
          productId: editedProduct.productId,
          variantId: variant?.variantId === null ? variant?.id : variant?.variantId,
          mrp: variant?.mrp,
          netPrice: variant?.netPrice,
          ...(variant?.unit && { unit: variant.unit }),
          available: variant?.available,
          requireParticipantInfo: variant?.requireParticipantInfo,
          ...(editedProduct?.alias && { alias: editedProduct.alias }), // Add alias from product level to each variant
        }))
      );

      console.log(allApiData);

      await axiosInstance.put(
        `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/products`,
        allApiData,
        {
          headers: {
            sessionToken: localStorage.getItem('sessionToken'),
            'X-Vendor-Id': user?.id,
            'X-USER-ROLE': USER_ROLES?.VENDOR,
          },
        }
      );

      const numChanges = changedProducts.size;
      setChangedProducts(new Map());
      setChangedVariants(new Set());
      setOriginalProducts(JSON.parse(JSON.stringify(products)));
      toast.success(`Successfully updated ${numChanges} product${numChanges > 1 ? 's' : ''}!`);
    } catch (error) {
      console.error(error);
      const errorMessage = error.response?.data || error.message || '';

      if (
        errorMessage.toLowerCase().includes('view only') ||
        errorMessage.toLowerCase().includes('store front')
      ) {
        setShowAccessErrorDialog(true);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardChanges = async () => {
    setProducts(JSON.parse(JSON.stringify(originalProducts)));
    setChangedProducts(new Map());
    setChangedVariants(new Set());

    if (pendingNavigation) {
      if (pendingNavigation.type === 'category') {
        await handleCategorySelect(pendingNavigation.categoryId);
      } else if (pendingNavigation.type === 'subcategory') {
        await handleSubCategorySelect(pendingNavigation.subCategoryId);
      } else if (pendingNavigation.type === 'subsubcategory') {
        await handleSubSubCategorySelect(pendingNavigation.subSubCategoryId);
      }
    }

    setShowConfirmDialog(false);
    setPendingNavigation(null);
  };

  const handleCategorySelect = async (categoryId) => {
    if (changedProducts.size > 0) {
      setPendingNavigation({ type: 'category', categoryId });
      setShowConfirmDialog(true);
      return;
    }

    const category = categories.find((c) => c.id === categoryId);
    if (!category) return;

    setSelectedCategory(categoryId);
    setSearchQuery('');

    const newSubCategories = category.subCategories || [];
    setSubCategories(newSubCategories);

    const firstSub = newSubCategories[0];
    setSelectedSubCategory(firstSub?.id || null);

    const newSubSubCategories = firstSub?.subCategories || [];
    setSubSubCategories(newSubSubCategories);
    setSelectedSubSubCategory(newSubSubCategories[0]?.id || null);

    const leafNode = findFirstLeafNode(category);
    if (leafNode) {
      await fetchProducts(categoryId, leafNode.id);
    } else {
      setProducts([]);
      setOriginalProducts([]);
    }
  };

  const handleSubCategorySelect = async (subCategoryId) => {
    if (changedProducts.size > 0) {
      setPendingNavigation({ type: 'subcategory', subCategoryId });
      setShowConfirmDialog(true);
      return;
    }

    const subCategory = subCategories.find((sc) => sc.id === subCategoryId);
    if (!subCategory) return;

    setSelectedSubCategory(subCategoryId);
    setSearchQuery('');

    const newSubSubCategories = subCategory.subCategories || [];
    setSubSubCategories(newSubSubCategories);
    setSelectedSubSubCategory(newSubSubCategories[0]?.id || null);

    const leafNode = findFirstLeafNode(subCategory);
    if (leafNode) {
      await fetchProducts(selectedCategory, leafNode.id);
    } else {
      setProducts([]);
      setOriginalProducts([]);
    }
  };

  const handleSubSubCategorySelect = async (subSubCategoryId) => {
    if (changedProducts.size > 0) {
      setPendingNavigation({ type: 'subsubcategory', subSubCategoryId });
      setShowConfirmDialog(true);
      return;
    }

    setSelectedSubSubCategory(subSubCategoryId);
    setSearchQuery('');
    await fetchProducts(selectedCategory, subSubCategoryId);
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      const currentIndex = categories.findIndex((c) => c.id === selectedCategory);
      if (currentIndex < categories.length - 1) {
        handleCategorySelect(categories[currentIndex + 1].id);
      }
    },
    onSwipedRight: () => {
      const currentIndex = categories.findIndex((c) => c.id === selectedCategory);
      if (currentIndex > 0) {
        handleCategorySelect(categories[currentIndex - 1].id);
      }
    },
    trackMouse: true,
  });

  const handleEditClick = (product) => {
    navigate(`/product/pricing/${product.productId}`, { state: { product } });
  };

  const handleClearAllFilters = () => {
    setSelectedFilters({
      tags: [],
      authors: [],
      genres: [],
      publishers: [],
      languages: [],
      collections: [],
      institutions: [],
      brands: [],
    });
  };

  const handleApplyFilters = (newFilters) => {
    setSelectedFilters(newFilters);
    setIsFilterSheetOpen(false);
  };

  const handleRemoveFilter = (type, valueToRemove) => {
    setSelectedFilters((prevFilters) => ({
      ...prevFilters,
      [type]: prevFilters[type].filter((value) => value !== valueToRemove),
    }));
  };

  const hasActiveFilters = Object.values(selectedFilters).some((arr) => arr.length > 0);
  const hasUnsavedChanges = changedProducts.size > 0;

  useEffect(() => {
    if (user?.id && user?.vendorCategories) {
      fetchCategoriesAndProducts();
    }
  }, [user?.id, user?.vendorCategories]);

  useEffect(() => {
    const applySortAndFilters = () => {
      let processedData = [...products];

      // 1. Apply Sorting
      processedData.sort((a, b) => {
        switch (sortOption) {
          case 'name-asc':
            return a.productName.localeCompare(b.productName);
          case 'name-desc':
            return b.productName.localeCompare(a.productName);
          case 'default':
          default: {
            const aIsAvailable = a.productVariants?.some((v) => v.available) ?? false;
            const bIsAvailable = b.productVariants?.some((v) => v.available) ?? false;
            if (aIsAvailable && !bIsAvailable) return -1;
            if (!aIsAvailable && bIsAvailable) return 1;
            return a.productName.localeCompare(b.productName);
          }
        }
      });

      // 2. Apply Search
      if (searchQuery) {
        processedData = processedData.filter((p) =>
          p?.productName?.toLowerCase().includes(searchQuery?.toLowerCase())
        );
      }

      // 3. Apply Filters
      const filterKeys = {
        tags: 'Tag',
        authors: 'Author',
        genres: 'Genre',
        publishers: 'Publisher',
        languages: 'Language',
        collections: 'Collection',
        institutions: 'Institution',
      };

      Object.entries(selectedFilters).forEach(([type, selectedValues]) => {
        if (selectedValues.length > 0) {
          if (type === 'brands') {
            // Handle brand filter separately since it's a direct product property
            processedData = processedData.filter((product) =>
              selectedValues.includes(product.brand)
            );
          } else {
            const attributeName = filterKeys[type];
            processedData = processedData.filter((product) => {
              const productValues =
                product.productAttributes
                  ?.filter((attr) => attr?.name === attributeName)
                  .map((attr) => attr?.value) || [];
              return selectedValues.some((selectedValue) => productValues.includes(selectedValue));
            });
          }
        }
      });

      setFilteredProducts(processedData);
    };

    applySortAndFilters();
  }, [products, searchQuery, selectedFilters, sortOption]);

  return (
    <div className="flex flex-col h-full">
      {hasUnsavedChanges && (
        <div className="fixed bottom-16 md:bottom-4 md:left-64 left-0 right-0 md:right-4 z-50 bg-white border border-orange-200 rounded-lg shadow-lg p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>

              <span className="text-sm font-semibold text-slate-700">
                {changedProducts.size} changes
              </span>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDiscardChanges}
                disabled={isSaving}
                className="text-red-600 border-red-300 hover:bg-red-50 bg-transparent"
              >
                <X className="h-4 w-4 mr-1" /> Discard{' '}
              </Button>

              <Button
                size="sm"
                onClick={handleBulkSave}
                disabled={isSaving}
                className="bg-[#F77C06] hover:bg-orange-600 text-white"
              >
                <Save className="h-4 w-4 mr-1" />
                {isSaving ? 'Saving...' : `Save All (${changedProducts.size})`}
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="px-4 pt-4 pb-2">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />

              <input
                type="text"
                placeholder="Search products in this category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-2 bg-white text-sm rounded-lg border border-slate-300 focus:outline-none focus:ring-1 focus:ring-[#F77C06] focus:border-[#F77C06] transition-all font-medium"
              />
            </div>
            {/* <>
              <ExcelActions
                products={filteredProducts}
                user={user}
                onImportSuccess={() => {
                  // Refresh products after import
                  if (selectedCategory && (selectedSubSubCategory || selectedSubCategory)) {
                    lastFetchedLeafId.current = null;
                    fetchProducts(selectedCategory, selectedSubSubCategory || selectedSubCategory);
                  }
                }}
              />
            </> */}
            <FilterSheet
              availableFilters={availableFilters}
              selectedFilters={selectedFilters}
              onApply={handleApplyFilters}
              isOpen={isFilterSheetOpen}
              onOpenChange={setIsFilterSheetOpen}
            />
            <SortDropdown currentSort={sortOption} onSortChange={setSortOption} />{' '}
          </div>
        </div>

        {categories.length > 0 && (
          <CategoryTabs
            categories={categories}
            selectedCategory={selectedCategory}
            onSelect={(id) => handleCategorySelect(id)}
          />
        )}
      </div>
      <div className="pb-16 md:pb-0 overflow-y-auto">
        {subCategories.length > 0 && (
          <SubCategoryMenu
            subCategories={subCategories}
            selectedSubCategory={selectedSubCategory}
            onSelect={handleSubCategorySelect}
          />
        )}

        {subSubCategories.length > 0 && (
          <SubCategoryMenu
            subCategories={subSubCategories}
            selectedSubCategory={selectedSubSubCategory}
            onSelect={handleSubSubCategorySelect}
          />
        )}

        <main className="flex-1 relative overflow-y-auto bg-white" {...handlers}>
          <div className="bg-white pb-2">
            <ActiveFiltersDisplay
              activeFilters={selectedFilters}
              onRemoveFilter={handleRemoveFilter}
              onClearAll={handleClearAllFilters}
            />
          </div>

          <div className="px-4 py-4 grid grid-cols-2 pb-20 md:pb-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
            {loading ? (
              <ProductListSkeleton />
            ) : filteredProducts.length > 0 ? (
              filteredProducts.map((product) => (
                <ProductCard
                  key={product.productId}
                  product={product}
                  changedVariants={changedVariants}
                  onEditClick={handleEditClick}
                />
              ))
            ) : (
              <EmptyState hasSearch={!!searchQuery} hasFilters={hasActiveFilters} />
            )}
          </div>
        </main>
      </div>
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="w-11/12 rounded-md">
          <div className="py-3 px-2 w-full text-center flex flex-col items-center max-w-full">
            <h3 className="text-base font-bold text-slate-900 mb-2">Unsaved Changes</h3>

            <p className="text-slate-600 mb-4">
              You have {changedProducts.size} product
              {changedProducts.size !== 1 ? 's' : ''} with unsaved changes. What would you like to
              do?
            </p>

            <div className="flex flex-wrap gap-2 justify-center">
              <Button
                variant="outline"
                onClick={() => {
                  setShowConfirmDialog(false);
                  setPendingNavigation(null);
                }}
                className="text-slate-600 px-2 py-1.5"
              >
                Cancel
              </Button>

              <Button
                variant="outline"
                onClick={() => {
                  handleDiscardChanges();
                  setShowConfirmDialog(false);
                }}
                className="text-red-600 px-2 py-1.5 border-red-300 hover:bg-red-50"
              >
                Discard
              </Button>

              <Button
                onClick={async () => {
                  await handleBulkSave();
                  setShowConfirmDialog(false);
                }}
                disabled={isSaving}
                className="bg-[#F77C06] py-1.5 px-2 hover:bg-orange-600 text-white"
              >
                {isSaving ? 'Saving...' : 'Save & Continue'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showAccessErrorDialog} onOpenChange={setShowAccessErrorDialog}>
        <DialogContent className="w-[90%] max-w-sm rounded-2xl p-4 gap-0 border-0 shadow-2xl">
          <div className="flex flex-col items-center text-center">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-50">
              <AlertTriangle className="h-5 w-5 text-red-500" strokeWidth={2} />
            </div>
            <DialogTitle className="text-base font-semibold text-slate-900 mb-1">
              Access Restricted
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Your account is in view-only mode. Contact your Administrator to enable editing.
            </DialogDescription>
          </div>
          <Button
            onClick={() => setShowAccessErrorDialog(false)}
            className="w-full mt-4 bg-[#F77C06] hover:bg-orange-600 text-white font-medium rounded-xl h-10"
          >
            Understood
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
